import {
  FamilyMember,
  Fetch,
  YearlyCategory,
  YearlyDisabilityProof,
  YearlyFee,
  YearlyLowIncome,
} from '../api-clients/api-types'
import { useApi } from '../api-clients/client-hooks'
import {
  postYearlyCategory,
  postYearlyDisabilityProof,
  postYearlyFee,
  postYearlyLowIncome,
} from '../api-clients/eba-clients'
import { useEmployee } from '../contexts/employee-context'
import { useFamilyMembers } from '../contexts/family-members-context'
import { trackEvent } from '../utils/tracking-utils'
import { valueOrNull } from '../utils/form-utils'

export function useFeePaid(
  year: number,
  successCallback?: (paid: boolean) => void
): [Fetch<YearlyFee>, (isPaid?: boolean) => void] {
  const { employee, setYearlyFee } = useEmployee()
  const [addFeeResponse, addFee] = useApi(postYearlyFee, {
    successCallback: (data) => {
      setYearlyFee(data)
      if (successCallback) {
        successCallback(data.isPaid)
      }
    },
  })

  const setFeePaid = (isPaid?: boolean) => {
    const feeIsPaid = employee?.yearlyFees?.find((fee) => fee.year === year)
    if (isPaid !== undefined && isPaid !== feeIsPaid?.isPaid) {
      trackEvent('Register-Fee-Paid', { category: 'Employee', year })
      addFee({
        id: employee.id,
        year: year.toString(),
        body: {
          isPaid,
        },
      })
      return true
    }
    return false
  }
  return [addFeeResponse, setFeePaid]
}

export function useDisabilityProof(
  year: number,
  successCallback?: (isRegistered: boolean) => void
): [Fetch<YearlyDisabilityProof>, (paid: boolean) => void] {
  const { employee, setYearlyDisabilityProof } = useEmployee()
  const [addDisabilityProofResponse, addDisabilityProof] = useApi(postYearlyDisabilityProof, {
    successCallback: (data) => {
      setYearlyDisabilityProof(data)
      if (successCallback) {
        successCallback(data.isRegistered)
      }
    },
  })

  const setDisabilityProof = (isRegistered?: boolean) => {
    const disabilityProofRegistered = employee?.sensitiveInfo?.disabilityProofs?.find(
      (proof) => proof.year === year
    )
    if (isRegistered !== undefined && disabilityProofRegistered?.isRegistered !== isRegistered) {
      trackEvent('Register-Disability-Proof', { category: 'Employee', year })
      addDisabilityProof({
        id: employee.id,
        year: year.toString(),
        body: {
          isRegistered,
        },
      })
      return true
    }
    return false
  }

  return [addDisabilityProofResponse, setDisabilityProof]
}

export function useCategory(
  year: number,
  successCallback?: (category: YearlyCategory) => void
): [Fetch<YearlyCategory>, (categoryId: string) => void] {
  const { employee, setYearlyCategories } = useEmployee()
  const [categoryResponse, addCategory] = useApi(postYearlyCategory, {
    successCallback: (data) => {
      setYearlyCategories(data)
      if (successCallback) {
        successCallback(data)
      }
    },
  })
  const existingCategory = employee?.categories?.find((c) => c.year === year)?.category

  const setCategory = (categoryId?: string): boolean => {
    if (!(categoryId === undefined || categoryId === '') && existingCategory?.id !== categoryId) {
      trackEvent('Register-Category', { category: 'Employee', year })
      addCategory({
        id: employee.id,
        year: year.toString(),
        body: {
          categoryId: valueOrNull(categoryId),
        },
      })
      return true
    }
    return false
  }

  return [categoryResponse, setCategory]
}

export function useLowIncome(
  year: number,
  familyMember?: FamilyMember,
  callback?: (data: YearlyLowIncome) => void
): [Fetch<YearlyLowIncome>, (isRegistered: boolean) => boolean] {
  const { setLowIncomeForFamilyMember } = useFamilyMembers()
  const [lowIncomeResponse, setYearlyLowIncome] = useApi(postYearlyLowIncome, {
    successCallback: (data: YearlyLowIncome) => {
      if (familyMember) setLowIncomeForFamilyMember(familyMember.id, data)
      if (callback) {
        callback(data)
      }
    },
  })

  const setLowIncome = (isRegistered: boolean): boolean => {
    const existingLowIncome = familyMember?.lowIncomes?.find((it) => it.year === year)
    const undefinedIsEqualFalse = existingLowIncome?.isRegistered === undefined && !isRegistered

    if (familyMember && !undefinedIsEqualFalse && existingLowIncome?.isRegistered !== isRegistered) {
      trackEvent('Register-Low-Income', { category: 'Family-Member', year })
      setYearlyLowIncome({
        id: familyMember.id,
        year: year.toString(),
        body: {
          year: year.toString(),
          isRegistered,
        },
      })
      return true
    }
    return false
  }

  return [lowIncomeResponse, setLowIncome]
}
